/**
 * 文件拖放区组件
 *
 * 提供拖拽投放 + 点击选择两种文件添加方式。
 * 仅负责收集 File 对象并回调，不做校验（由 useFileUploadManager 处理）。
 */
import React, { useState, useRef, useCallback } from "react";
import { InboxOutlined, PlusOutlined } from "@ant-design/icons";
import { Typography, Button } from "antd";
import { VALIDATION_CONSTANTS } from "./fileValidation";

const { Text } = Typography;

const FileDropZone = ({ onFilesAdded, disabled = false }) => {
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);
    const dragCounter = useRef(0);

    const acceptStr = VALIDATION_CONSTANTS.ALLOWED_EXTENSIONS.join(",");

    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current++;
        if (dragCounter.current === 1) setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current--;
        if (dragCounter.current === 0) setIsDragging(false);
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current = 0;
        setIsDragging(false);

        if (disabled) return;
        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            onFilesAdded?.(files);
        }
    }, [disabled, onFilesAdded]);

    const handleClick = useCallback(() => {
        if (!disabled) fileInputRef.current?.click();
    }, [disabled]);

    const handleFileSelect = useCallback((e) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            onFilesAdded?.(files);
        }
        // 重置 input 以支持重复选择同一文件
        e.target.value = "";
    }, [onFilesAdded]);

    return (
        <div
            className={`upload-dropzone ${isDragging ? "upload-dropzone--dragging" : ""} ${disabled ? "upload-dropzone--disabled" : ""}`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={handleClick}
        >
            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={acceptStr}
                style={{ display: "none" }}
                onChange={handleFileSelect}
            />

            <div className="upload-dropzone__content">
                {isDragging ? (
                    <>
                        <InboxOutlined className="upload-dropzone__icon upload-dropzone__icon--active" />
                        <Text strong style={{ color: "#1890ff" }}>释放文件以开始上传</Text>
                    </>
                ) : (
                    <>
                        <InboxOutlined className="upload-dropzone__icon" />
                        <Text strong>拖拽文件到此处，或点击选择文件</Text>
                        <Text type="secondary" style={{ fontSize: 12, marginTop: 4 }}>
                            支持 PDF、Excel (.xlsx)、Word (.docx)、图片，单文件最大 {VALIDATION_CONSTANTS.MAX_FILE_SIZE_MB}MB
                        </Text>
                    </>
                )}
            </div>
        </div>
    );
};

export default FileDropZone;
